import { useState } from 'react'
import { Eye, EyeOff, Info, Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGetApiMe, usePostApiUsersUserIdPassword } from '@/api/generated/user/user'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { notify } from '@/lib/notify'

interface PasswordRuleTest {
  key: string
  test: (pw: string) => boolean
}

const PASSWORD_RULE_TESTS: PasswordRuleTest[] = [
  { key: 'minLength', test: (pw) => pw.length >= 8 },
  { key: 'uppercase', test: (pw) => /[A-Z]/.test(pw) },
  { key: 'lowercase', test: (pw) => /[a-z]/.test(pw) },
  { key: 'number', test: (pw) => /[0-9]/.test(pw) },
  { key: 'special', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
]

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  trailing,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  autoComplete?: string
  trailing?: React.ReactNode
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={id}>{label}</Label>
        {trailing}
      </div>
      <div className="relative">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  )
}

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export function PasswordChangeDialog({ isOpen, onOpenChange }: Props) {
  const { t } = useTranslation(['profile', 'common'])
  const { data: meData } = useGetApiMe()
  const me = meData?.data

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { mutateAsync: changePassword, isPending } = usePostApiUsersUserIdPassword()

  const passwordRules = PASSWORD_RULE_TESTS.map((r) => ({ ...r, label: t(`passwordDialog.rules.${r.key}`) }))
  const passwordValid = PASSWORD_RULE_TESTS.every((r) => r.test(newPassword))
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword
  const canSubmit = !!currentPassword && passwordValid && passwordsMatch && !isPending

  const reset = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) reset()
    onOpenChange(open)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!me?.id) return
    if (!passwordValid) {
      setError(t('passwordDialog.errors.invalid'))
      return
    }
    if (!passwordsMatch) {
      setError(t('passwordDialog.errors.mismatch'))
      return
    }
    setError(null)
    try {
      await changePassword({ userId: me.id, data: { currentPassword, newPassword } })
      notify.success(t('passwordDialog.toasts.success'))
      reset()
      onOpenChange(false)
    } catch (err) {
      notify.apiError(err, t('passwordDialog.toasts.error'))
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('passwordDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('passwordDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <p className="text-sm text-destructive">{error}</p>}

          <PasswordField
            id="current-password"
            label={t('passwordDialog.fields.current')}
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
          />

          <PasswordField
            id="new-password"
            label={t('passwordDialog.fields.new')}
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            trailing={
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={passwordValid ? 'text-green-500' : newPassword ? 'text-destructive' : 'text-muted-foreground'}
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3" align="end">
                  <p className="text-xs font-medium mb-2">{t('passwordDialog.rules.title')}</p>
                  <ul className="space-y-1">
                    {passwordRules.map((rule) => {
                      const ok = rule.test(newPassword)
                      return (
                        <li key={rule.key} className="flex items-center gap-2 text-xs">
                          {ok
                            ? <Check className="h-3 w-3 text-green-500 shrink-0" />
                            : <X className="h-3 w-3 text-destructive shrink-0" />}
                          <span className={ok ? 'text-green-600' : 'text-muted-foreground'}>
                            {rule.label}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </PopoverContent>
              </Popover>
            }
          />

          <PasswordField
            id="confirm-password"
            label={t('passwordDialog.fields.confirm')}
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? t('buttons.saving', { ns: 'common' }) : t('passwordDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
